// lib/inspectorPool.mjs — R120 (team: "give someone inspection and housekeeping
// access"; Madel: "changed her role to Inspector, still not in the dropdown") —
// ONE list of the people an inspection can be assigned to, shared by every
// inspector picker: the Inspections queue + Schedule picker
// (InspectionsPage.inspectorOpts), the Calendar's inspections layer (Assign
// sheet + "+ Inspection" chooser) and the Ops board. Before R120 the three
// read three different sources (profiles by role / ao_managers / profiles by
// role), so a cleaner who also inspects ("Lacey(IHC)") was assignable on one
// board and invisible on the others.
//
// Contract C7 (frozen):
//   inspectorPool({ profiles:[{full_name, email, role, is_active}],
//                   roster:[{name, email, active}], jobNames:string[] })
//     → [{ name, hint:'inspector'|'roster'|'lead'|'other', role }]
//   poolLabel(p) → the option label ("Name" / "Name (manager)").
//
// Order (deduped case-insensitively on the NAME, first wins):
//   1  active logins whose role is 'inspector'                 (hint 'inspector')
//   2  active rows of the inspector roster (ao_managers). A row whose email
//      equals an ACTIVE login's email is emitted under that login's full_name
//      — so a pick lands under the name the login's own "My inspections"
//      matches; otherwise the row's own name                  (hint 'roster')
//   3  admin / director / manager logins (leadership walks homes too) (hint 'lead')
//   4  names already on jobs (assignments store a free-text NAME) (hint 'other')
// Never returns an email: a login with no full_name is skipped (a name with an
// '@' — the assignees list falls back to the email — is never a name). A
// remote_ops login is assignable only when it is on the roster (D22).
// `role` is the login's profile role when the entry is a login (an email-
// matched roster row carries its login's role), 'roster' for a roster row with
// no login behind it, 'other' for a job name.
// Pure (no I/O); types in lib/inspectorPool.d.mts; tests: node lib/inspectorPool.test.mjs.

export const LEAD_ROLES = ['admin', 'director', 'manager'];

const NAME_MAX = 80;
const cleanName = (s) => {
  const n = String(s == null ? '' : s).replace(/\s+/g, ' ').trim();
  if (!n || n.includes('@')) return '';
  return n.length > NAME_MAX ? n.slice(0, NAME_MAX).trim() : n;
};
const normEmail = (s) => String(s == null ? '' : s).trim().toLowerCase();
const roleOf = (p) => String((p && p.role) || '').trim().toLowerCase();

export function inspectorPool({ profiles = [], roster = [], jobNames = [] } = {}) {
  const seen = new Set();
  const out = [];
  const push = (name, hint, role) => {
    const n = cleanName(name);
    if (!n) return;
    const k = n.toLowerCase();
    if (seen.has(k)) return;
    seen.add(k);
    out.push({ name: n, hint, role });
  };
  const logins = (Array.isArray(profiles) ? profiles : []).filter(p => p && p.is_active !== false);
  // email → the first active login carrying it (and a usable full name)
  const byEmail = new Map();
  for (const p of logins) {
    const em = normEmail(p.email);
    if (em && !byEmail.has(em)) byEmail.set(em, p);
  }
  // 1 · inspector logins
  for (const p of logins) if (roleOf(p) === 'inspector') push(p.full_name, 'inspector', 'inspector');
  // 2 · the inspector roster (active rows), under the login's name when the email matches one
  for (const r of (Array.isArray(roster) ? roster : [])) {
    if (!r || r.active === false || r.archived) continue;
    const login = byEmail.get(normEmail(r.email)) || null;
    const loginName = login ? cleanName(login.full_name) : '';
    if (loginName) push(loginName, 'roster', roleOf(login) || 'roster');
    else push(r.name, 'roster', 'roster');
  }
  // 3 · leads
  for (const role of LEAD_ROLES) for (const p of logins) if (roleOf(p) === role) push(p.full_name, 'lead', role);
  // 4 · names already on jobs
  for (const j of (Array.isArray(jobNames) ? jobNames : [])) push(j, 'other', 'other');
  return out;
}

// The picker label: a lead keeps the "(manager)" suffix the Inspections page
// always showed (an email-matched roster row of a manager login reads the same).
export function poolLabel(p) {
  if (!p) return '';
  const name = String(p.name || '');
  const role = String(p.role || '').toLowerCase();
  return LEAD_ROLES.includes(role) ? `${name} (${role})` : name;
}
